import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  useEscapeStack,
  useIntersectionObserver,
  useWheelScrollProxy,
} from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon, CloseIcon } from '#/shared/ui/icons'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'
import { Skeleton } from '#/shared/ui/skeleton'
import { getPortalContainer } from '#/shared/lib/portal-container'

export interface MultiSelectOption {
  value: string
  label: string
  tag?: string
  disabled?: boolean
  accent?: 'new'
}

export interface MultiSelectProps {
  label?: string
  labelSize?: LabelSize
  options: MultiSelectOption[]
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
  onOpenChange?: (open: boolean) => void
  loading?: boolean
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  error?: boolean
  max?: number
  searchable?: boolean
  filter?: (option: MultiSelectOption, query: string) => boolean
  searchValue?: string
  onSearchChange?: (query: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isFetchingMore?: boolean
  emptyMessage?: React.ReactNode
  className?: string
  id?: string
  name?: string
  allowCreate?: boolean
  onCreateOption?: (name: string) => void
  /**
   * Render the option list through a portal to `document.body`, positioned
   * against the control. Required inside any `overflow-hidden` or scrollable
   * ancestor — an accordion, a dialog, a scrolling side panel — which would
   * otherwise clip the absolutely-positioned list.
   */
  portal?: boolean
}

const SKELETON_ROW_COUNT = 3

const CONTROL_CLASS = 'min-h-8 gap-1.25 px-1.25 py-0.75 flex-wrap'

function defaultFilter(option: MultiSelectOption, query: string): boolean {
  if (!query) return true
  return option.label.toLowerCase().includes(query.toLowerCase())
}

function useControlled<T>(
  controlled: T | undefined,
  defaultValue: T,
): [T, (next: T) => void] {
  const isControlled = controlled !== undefined
  const [internal, setInternal] = React.useState<T>(defaultValue)
  const value = isControlled ? (controlled as T) : internal
  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next)
    },
    [isControlled],
  )
  return [value, setValue]
}

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean,
  /** The portaled panel, which lives outside `ref` but is still "inside". */
  panelRef?: React.RefObject<HTMLElement | null>,
) {
  React.useEffect(() => {
    if (!enabled) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!ref.current || !target) return
      if (ref.current.contains(target)) return
      if (panelRef?.current?.contains(target) === true) return
      handler()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [ref, handler, enabled, panelRef])
}

function SelectedTag({
  option,
  onRemove,
  disabled,
}: {
  option: MultiSelectOption
  onRemove: () => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'flex shrink-0 animate-pop-in items-center gap-1 rounded-sm px-1.25 text-xs text-gray-900',
        option.accent === 'new' ? 'bg-orange-100' : 'bg-blue-100',
      )}
    >
      {option.label}
      {!disabled && (
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onRemove}
          aria-label={t('multiSelect.removeItem', { label: option.label })}
          className="flex size-3 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-gray-900"
        >
          <CloseIcon className="block size-2" />
        </button>
      )}
    </span>
  )
}

function TagList({
  selected,
  onRemove,
  disabled,
}: {
  selected: MultiSelectOption[]
  onRemove: (value: string) => void
  disabled: boolean
}) {
  if (selected.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1">
      {selected.map((option) => (
        <SelectedTag
          key={option.value}
          option={option}
          onRemove={() => onRemove(option.value)}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

function CheckMark({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-sm border transition-colors',
        checked
          ? 'border-primary bg-primary text-white'
          : 'border-gray-200 bg-white',
      )}
      aria-hidden="true"
    >
      {checked && (
        <svg
          viewBox="0 0 10 10"
          fill="none"
          className="size-2.5"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.5 5.2L4 7.5L8.5 2.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  )
}

function OptionRow({
  option,
  index,
  listboxId,
  isActive,
  isSelected,
  onHover,
  onToggle,
}: {
  option: MultiSelectOption
  index: number
  listboxId: string
  isActive: boolean
  isSelected: boolean
  onHover: (i: number) => void
  onToggle: (option: MultiSelectOption) => void
}) {
  return (
    <li
      id={`${listboxId}-opt-${index}`}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => onHover(index)}
      onClick={() => onToggle(option)}
      className={cn(
        'flex h-9 shrink-0 cursor-pointer items-center gap-2.5 rounded-sm px-2.5 text-sm text-gray-900',
        isActive && 'bg-gray-100',
        option.disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <CheckMark checked={isSelected} />
      <span className="flex-1 truncate">{option.label}</span>
      {option.tag && (
        <span className="flex items-center justify-center rounded-sm bg-gray-100 px-1.25 py-0.5 text-sm text-gray-500">
          {option.tag}
        </span>
      )}
    </li>
  )
}

const SKELETON_WIDTHS = ['w-32', 'w-24', 'w-40']

function OptionListSkeleton() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <li
          key={i}
          className="flex h-9 shrink-0 animate-in animate-pulse items-center gap-2.5 rounded-sm px-2.5 fade-in duration-200 ease-out"
        >
          <Skeleton className="size-4 shrink-0 rounded-xs" />
          <Skeleton
            className={cn('h-3', SKELETON_WIDTHS[i % SKELETON_WIDTHS.length])}
          />
        </li>
      ))}
    </>
  )
}

function LoadMoreSentinel({
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}) {
  const setNode = useIntersectionObserver({
    enabled: hasMore && !isFetchingMore,
    rootMargin: '32px',
    onIntersect: onLoadMore,
  })
  if (!hasMore && !isFetchingMore) return null
  return (
    <li
      ref={setNode}
      aria-hidden="true"
      className="flex h-8 shrink-0 items-center px-2"
    >
      {isFetchingMore && <Skeleton className="h-3 w-32" />}
    </li>
  )
}

interface OptionListProps {
  listboxId: string
  filtered: MultiSelectOption[]
  selectedValues: string[]
  activeIndex: number
  loading: boolean
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore?: () => void
  onHover: (i: number) => void
  onToggle: (option: MultiSelectOption) => void
  emptyMessage?: React.ReactNode
  createQuery?: string
  onCreate?: (name: string) => void
}

function OptionRows({
  filtered,
  selectedSet,
  activeIndex,
  listboxId,
  onHover,
  onToggle,
}: {
  filtered: MultiSelectOption[]
  selectedSet: Set<string>
  activeIndex: number
  listboxId: string
  onHover: (i: number) => void
  onToggle: (option: MultiSelectOption) => void
}) {
  return (
    <>
      {filtered.map((option, i) => (
        <OptionRow
          key={option.value}
          option={option}
          index={i}
          listboxId={listboxId}
          isActive={i === activeIndex}
          isSelected={selectedSet.has(option.value)}
          onHover={onHover}
          onToggle={onToggle}
        />
      ))}
    </>
  )
}

function EmptyRow({ message }: { message: React.ReactNode }) {
  if (typeof message === 'string') {
    return (
      <li className="flex h-8 shrink-0 animate-in items-center px-2 text-sm text-gray-500 fade-in duration-200 ease-out">
        {message}
      </li>
    )
  }
  return (
    <li className="shrink-0 animate-in fade-in duration-200 ease-out">
      {message}
    </li>
  )
}

// eslint-disable-next-line complexity
function OptionList({
  listboxId,
  filtered,
  selectedValues,
  activeIndex,
  loading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onHover,
  onToggle,
  emptyMessage,
  createQuery,
  onCreate,
}: OptionListProps) {
  const { t } = useTranslation()
  const hasOptions = filtered.length > 0
  const selectedSet = React.useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  )
  const trimmedQuery = createQuery?.trim() ?? ''
  const exactMatch = trimmedQuery
    ? filtered.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase())
    : true
  const createHandler =
    onCreate && trimmedQuery.length > 0 && !exactMatch && !loading
      ? onCreate
      : undefined
  const scrollRef = React.useRef<HTMLDivElement>(null)
  // Keep wheel-scrolling alive when the panel is portaled inside a Radix
  // dialog, whose scroll lock otherwise eats wheel events on out-of-tree
  // content. Same fix as the Select panel.
  useWheelScrollProxy(scrollRef)
  return (
    <div
      ref={scrollRef}
      className="flex min-h-0 flex-1 flex-col gap-1.25 overflow-auto p-1"
    >
      {createHandler && (
        <CreateRow query={trimmedQuery} onCreate={createHandler} />
      )}
      <ul
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        className="flex min-h-0 flex-1 flex-col gap-0.5"
      >
        {loading && !hasOptions && <OptionListSkeleton />}
        {!loading && !hasOptions && !createHandler && (
          <EmptyRow message={emptyMessage ?? t('multiSelect.noResults')} />
        )}
        {hasOptions && (
          <OptionRows
            filtered={filtered}
            selectedSet={selectedSet}
            activeIndex={activeIndex}
            listboxId={listboxId}
            onHover={onHover}
            onToggle={onToggle}
          />
        )}
        {onLoadMore && (
          <LoadMoreSentinel
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            onLoadMore={onLoadMore}
          />
        )}
      </ul>
    </div>
  )
}

function CreateRow({
  query,
  onCreate,
}: {
  query: string
  onCreate: (name: string) => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onCreate(query)}
      className="flex h-8 shrink-0 cursor-pointer items-center gap-1.25 rounded-sm bg-warm-50 px-1.25 text-left text-sm text-gray-900"
    >
      <span>{t('multiSelect.createLabel')}</span>
      <span className="flex items-center justify-center rounded-sm bg-blue-100 px-1.25 py-0.5 text-sm text-gray-900">
        {query}
      </span>
    </button>
  )
}

const PANEL_CLASSES =
  'flex max-h-60 flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-popover'

const PANEL_MAX_HEIGHT = 240
const PANEL_GAP = 4
const VIEWPORT_MARGIN = 8

/**
 * Fixed coordinates under (or over) the control, anchored to its live rect.
 *
 * Flips above the control when the space below cannot hold the panel but the
 * space above can, so a picker near the bottom of the viewport still opens
 * fully rather than into the fold.
 */
function panelStyle(rect: DOMRect | null): React.CSSProperties {
  if (!rect) return { position: 'fixed', top: 0, left: 0, opacity: 0 }
  const below = window.innerHeight - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN
  const above = rect.top - PANEL_GAP - VIEWPORT_MARGIN
  const flip = below < PANEL_MAX_HEIGHT && above > below
  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    // A modal dialog puts `pointer-events: none` on <body> while open, and a
    // panel portaled under <body> inherits it — the list rendered but every
    // click fell through to the overlay behind. Same fix as the Popover panel.
    pointerEvents: 'auto',
    maxHeight: Math.max(0, Math.min(PANEL_MAX_HEIGHT, flip ? above : below)),
    ...(flip
      ? { bottom: window.innerHeight - rect.top + PANEL_GAP }
      : { top: rect.bottom + PANEL_GAP }),
  }
}

/** Track the control's viewport rect while the panel is open. */
function useAnchorRect(
  anchorRef: React.RefObject<HTMLElement | null>,
  open: boolean,
): DOMRect | null {
  const [rect, setRect] = React.useState<DOMRect | null>(null)
  React.useLayoutEffect(() => {
    if (!open) return
    const measure = () => {
      const node = anchorRef.current
      if (node) setRect(node.getBoundingClientRect())
    }
    measure()
    // `true` catches scrolls on every ancestor, not just the window — the
    // whole reason to portal is that one of those ancestors is scrollable.
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [anchorRef, open])
  return rect
}

function DropdownPanel({
  children,
  portal,
  anchorRef,
  panelRef,
  open,
}: {
  children: React.ReactNode
  portal: boolean
  anchorRef: React.RefObject<HTMLElement | null>
  panelRef: React.RefObject<HTMLDivElement | null>
  open: boolean
}) {
  const rect = useAnchorRect(anchorRef, open && portal)
  if (!portal) {
    return (
      <div
        ref={panelRef}
        className={cn(
          'absolute left-0 right-0 top-full z-50 mt-1',
          PANEL_CLASSES,
        )}
      >
        {children}
      </div>
    )
  }
  // z-80 (popout layer): the panel has to clear dialogs and the detail
  // sidebar, which is the situation that forced it out of the normal flow to
  // begin with. See the z-index scale in app/styles/index.css.
  return createPortal(
    <div
      ref={panelRef}
      style={panelStyle(rect)}
      className={cn('z-[80]', PANEL_CLASSES)}
    >
      {children}
    </div>,
    getPortalContainer(),
  )
}

interface MultiSelectDropdownProps {
  state: MultiSelectState
  handlers: ReturnType<typeof useHandlers>
  listboxId: string
  portal: boolean
  panelRef: React.RefObject<HTMLDivElement | null>
  emptyMessage?: React.ReactNode
  loading: boolean
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore?: () => void
  allowCreate?: boolean
  onCreateOption?: (name: string) => void
}

function MultiSelectDropdown({
  state,
  handlers,
  listboxId,
  portal,
  panelRef,
  emptyMessage,
  loading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  allowCreate,
  onCreateOption,
}: MultiSelectDropdownProps) {
  const handleCreate =
    allowCreate && onCreateOption
      ? (name: string) => {
          onCreateOption(name)
          state.setQuery('')
        }
      : undefined
  const handleToggle = (option: MultiSelectOption) => {
    handlers.toggle(option)
    state.setQuery('')
  }
  return (
    <DropdownPanel
      portal={portal}
      anchorRef={state.controlRef}
      panelRef={panelRef}
      open={state.open}
    >
      <OptionList
        listboxId={listboxId}
        filtered={state.filtered}
        selectedValues={state.selected}
        activeIndex={state.activeIndex}
        loading={loading}
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        onLoadMore={onLoadMore}
        onHover={state.setActiveIndex}
        onToggle={handleToggle}
        emptyMessage={emptyMessage}
        createQuery={allowCreate ? state.query : undefined}
        onCreate={handleCreate}
      />
    </DropdownPanel>
  )
}

function ChevronToggle({
  open,
  disabled,
  onClick,
}: {
  open: boolean
  disabled: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      tabIndex={-1}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-slot="chevron-toggle"
      className="flex shrink-0 cursor-pointer items-center justify-center px-0.75 py-2 text-gray-500 disabled:cursor-not-allowed"
      aria-label={t('multiSelect.toggleOptions')}
    >
      <ChevronDownIcon
        className={cn(
          'h-1.25 w-2.5 transition-transform',
          open && 'rotate-180',
        )}
      />
    </button>
  )
}

function getControlClassName(
  error: boolean,
  disabled: boolean,
  open: boolean,
  className?: string,
) {
  return cn(
    'flex w-full items-center rounded-sm border bg-white',
    CONTROL_CLASS,
    error ? 'border-red-500 focus-within:focus-ring-error' : 'border-gray-200',
    open && !error && 'focus-ring',
    disabled && 'cursor-not-allowed opacity-50',
    className,
  )
}

type MultiSelectState = {
  selected: string[]
  setSelected: (next: string[]) => void
  query: string
  setQuery: (q: string) => void
  open: boolean
  setOpen: (o: boolean) => void
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  filtered: MultiSelectOption[]
  searchRef: React.RefObject<HTMLInputElement | null>
  controlRef: React.RefObject<HTMLDivElement | null>
}

function useSearchQuery(
  searchValue: string | undefined,
  onSearchChange: ((q: string) => void) | undefined,
) {
  const [internalQuery, setInternalQuery] = React.useState('')
  const isControlled = onSearchChange !== undefined
  const query = isControlled ? (searchValue ?? '') : internalQuery
  const setQuery = React.useCallback(
    (next: string) => {
      if (isControlled) onSearchChange(next)
      else setInternalQuery(next)
    },
    [isControlled, onSearchChange],
  )
  return { query, setQuery, isControlled }
}

function useMultiSelectState({
  value,
  defaultValue,
  options,
  filter,
  searchValue,
  onSearchChange,
  onValueChange,
}: {
  value?: string[]
  defaultValue: string[]
  options: MultiSelectOption[]
  filter: (o: MultiSelectOption, q: string) => boolean
  searchValue?: string
  onSearchChange?: (q: string) => void
  onValueChange?: (v: string[]) => void
}) {
  const [selected, setSelectedRaw] = useControlled(value, defaultValue)
  const {
    query,
    setQuery,
    isControlled: isSearchControlled,
  } = useSearchQuery(searchValue, onSearchChange)
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const searchRef = React.useRef<HTMLInputElement | null>(null)
  const controlRef = React.useRef<HTMLDivElement | null>(null)

  const filtered = React.useMemo(
    () =>
      isSearchControlled ? options : options.filter((o) => filter(o, query)),
    [options, query, filter, isSearchControlled],
  )

  React.useEffect(() => {
    if (!open) {
      setActiveIndex(-1)
      if (!isSearchControlled) setQuery('')
    }
  }, [open, isSearchControlled, setQuery])

  React.useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(filtered.length - 1)
  }, [filtered.length, activeIndex])

  const commit = React.useCallback(
    (next: string[]) => {
      setSelectedRaw(next)
      onValueChange?.(next)
    },
    [setSelectedRaw, onValueChange],
  )

  return {
    selected,
    setSelected: commit,
    query,
    setQuery,
    open,
    setOpen,
    activeIndex,
    setActiveIndex,
    filtered,
    searchRef,
    controlRef,
  }
}

function moveActive(
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>,
  count: number,
  delta: number,
) {
  if (count === 0) return
  setActiveIndex((prev) => {
    const next = prev + delta
    if (next < 0) return count - 1
    if (next >= count) return 0
    return next
  })
}

const ARROW_DELTA: Partial<Record<string, number>> = {
  ArrowDown: 1,
  ArrowUp: -1,
}

function handleArrowKey(
  e: React.KeyboardEvent<HTMLInputElement>,
  state: MultiSelectState,
  delta: number,
) {
  e.preventDefault()
  if (!state.open) state.setOpen(true)
  moveActive(state.setActiveIndex, state.filtered.length, delta)
}

function handleEnterKey(
  e: React.KeyboardEvent<HTMLInputElement>,
  state: MultiSelectState,
  onToggle: (option: MultiSelectOption) => void,
) {
  if (
    !state.open ||
    state.activeIndex < 0 ||
    state.activeIndex >= state.filtered.length
  )
    return false
  e.preventDefault()
  onToggle(state.filtered[state.activeIndex])
  return true
}

function handleEnterOrCreate(
  e: React.KeyboardEvent<HTMLInputElement>,
  state: MultiSelectState,
  onToggle: (option: MultiSelectOption) => void,
  onCreate?: () => boolean,
) {
  if (handleEnterKey(e, state, onToggle)) return
  if (onCreate?.()) e.preventDefault()
}

function useKeyHandler({
  state,
  disabled,
  onToggle,
  onBackspace,
  onCreate,
}: {
  state: MultiSelectState
  disabled: boolean
  onToggle: (option: MultiSelectOption) => void
  onBackspace: () => void
  onCreate?: () => boolean
}) {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    const delta = ARROW_DELTA[e.key]
    if (delta !== undefined) {
      handleArrowKey(e, state, delta)
      return
    }
    if (e.key === 'Enter') {
      handleEnterOrCreate(e, state, onToggle, onCreate)
      return
    }
    if (e.key === 'Escape' && state.open) {
      e.preventDefault()
      state.setOpen(false)
      return
    }
    if (e.key === 'Backspace' && state.query === '') {
      e.preventDefault()
      onBackspace()
    }
  }
}

function useHandlers({
  state,
  options,
  max,
  disabled,
}: {
  state: MultiSelectState
  options: MultiSelectOption[]
  max?: number
  disabled: boolean
}) {
  const optionByValue = React.useMemo(() => {
    const map = new Map<string, MultiSelectOption>()
    for (const o of options) map.set(o.value, o)
    return map
  }, [options])

  const toggle = React.useCallback(
    (option: MultiSelectOption) => {
      if (option.disabled) return
      const already = state.selected.includes(option.value)
      if (already) {
        state.setSelected(state.selected.filter((v) => v !== option.value))
        return
      }
      if (max !== undefined && state.selected.length >= max) return
      state.setSelected([...state.selected, option.value])
    },
    [state, max],
  )

  const remove = React.useCallback(
    (value: string) => {
      state.setSelected(state.selected.filter((v) => v !== value))
    },
    [state],
  )

  const removeLast = React.useCallback(() => {
    if (state.selected.length === 0) return
    state.setSelected(state.selected.slice(0, -1))
  }, [state])

  const toggleOpen = React.useCallback(() => {
    if (disabled) return
    state.setOpen(!state.open)
  }, [disabled, state])

  return { toggle, remove, removeLast, toggleOpen, optionByValue }
}

interface ControlProps {
  selectedOptions: MultiSelectOption[]
  placeholder?: string
  disabled: boolean
  error: boolean
  open: boolean
  className?: string
  controlId: string
  listboxId: string
  controlRef: React.RefObject<HTMLDivElement | null>
  searchable: boolean
  query: string
  onQueryChange: (value: string) => void
  searchRef: React.RefObject<HTMLInputElement | null>
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onOpen: () => void
  onToggleOpen: () => void
  onRemove: (value: string) => void
}

function Control({
  selectedOptions,
  placeholder,
  disabled,
  error,
  open,
  className,
  controlId,
  listboxId,
  controlRef,
  searchable,
  query,
  onQueryChange,
  searchRef,
  onInputKeyDown,
  onOpen,
  onToggleOpen,
  onRemove,
}: ControlProps) {
  const focusInput = () => {
    if (disabled) return
    onOpen()
    searchRef.current?.focus()
  }
  // The arrow is a true toggle: open ⇒ close, closed ⇒ open with the search
  // focused. It is skipped by the wrapper's mousedown below on purpose —
  // letting both fire opened the list on press and closed it on release
  // (DEV-828).
  const onChevronClick = () => {
    if (open) onToggleOpen()
    else focusInput()
  }
  return (
    <div
      ref={controlRef}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-disabled={disabled || undefined}
      onMouseDown={(e) => {
        if (disabled) return
        const target = e.target as HTMLElement
        if (target.closest('[data-slot="clear-button"]')) return
        if (target.closest('[data-slot="chevron-toggle"]')) return
        if (target instanceof HTMLInputElement) return
        e.preventDefault()
        focusInput()
      }}
      data-slot="multi-select-trigger"
      className={getControlClassName(error, disabled, open, className)}
    >
      <TagList
        selected={selectedOptions}
        onRemove={onRemove}
        disabled={disabled}
      />
      {searchable ? (
        <input
          id={controlId}
          ref={searchRef}
          type="text"
          autoComplete="off"
          disabled={disabled}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onOpen}
          onKeyDown={onInputKeyDown}
          placeholder={selectedOptions.length === 0 ? placeholder : undefined}
          className="min-w-20 flex-1 bg-transparent text-sm placeholder:text-gray-300 focus-visible:outline-none disabled:cursor-not-allowed"
        />
      ) : (
        selectedOptions.length === 0 && (
          <span className="min-w-0 flex-1 truncate text-left text-sm text-gray-300 select-none">
            {placeholder}
          </span>
        )
      )}
      <ChevronToggle open={open} disabled={disabled} onClick={onChevronClick} />
    </div>
  )
}

function LabelRow({
  label,
  labelSize,
  htmlFor,
}: {
  label: string
  labelSize: LabelSize
  htmlFor: string
}) {
  return (
    <Label htmlFor={htmlFor} size={labelSize}>
      {label}
    </Label>
  )
}

function useMultiSelectApi(
  props: MultiSelectProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const {
    options,
    value,
    defaultValue,
    onValueChange,
    disabled = false,
    max,
    searchable = true,
    filter = defaultFilter,
    searchValue,
    onSearchChange,
  } = props

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)

  const state = useMultiSelectState({
    value,
    defaultValue: defaultValue ?? [],
    options,
    filter,
    searchValue,
    onSearchChange,
    onValueChange,
  })

  const handlers = useHandlers({ state, options, max, disabled })

  const selectedOptions = React.useMemo(
    () =>
      state.selected
        .map((v) => handlers.optionByValue.get(v))
        .filter((o): o is MultiSelectOption => !!o),
    [state.selected, handlers.optionByValue],
  )

  React.useImperativeHandle(
    ref,
    () => state.controlRef.current as HTMLDivElement,
    [state.controlRef],
  )

  useClickOutside(wrapperRef, () => state.setOpen(false), state.open, panelRef)

  // Escape closes the open dropdown before any surface beneath it (e.g. the
  // inline filters panel) — the shared stack layers by open order, and the
  // input's own Escape handler only fires when the search field has focus.
  const closeDropdown = React.useCallback(
    () => state.setOpen(false),
    [state.setOpen],
  )
  useEscapeStack(state.open, closeDropdown)

  React.useEffect(() => {
    if (state.open && searchable) state.searchRef.current?.focus()
  }, [state.open, searchable, state.searchRef])

  const { allowCreate, onCreateOption } = props
  const onCreate = React.useCallback((): boolean => {
    const trimmed = state.query.trim()
    if (!trimmed) return false
    const exactMatch = state.filtered.find(
      (o) => o.label.toLowerCase() === trimmed.toLowerCase(),
    )
    if (exactMatch) {
      handlers.toggle(exactMatch)
      state.setQuery('')
      return true
    }
    if (!allowCreate || !onCreateOption) return false
    onCreateOption(trimmed)
    state.setQuery('')
    return true
  }, [allowCreate, onCreateOption, state, handlers])

  const onKeyDown = useKeyHandler({
    state,
    disabled,
    onToggle: (option) => {
      handlers.toggle(option)
      state.setQuery('')
    },
    onBackspace: handlers.removeLast,
    onCreate,
  })

  return { state, handlers, selectedOptions, wrapperRef, panelRef, onKeyDown }
}

function useNotifyOpenChange(
  open: boolean,
  onOpenChange?: (open: boolean) => void,
) {
  const onOpenChangeRef = React.useRef(onOpenChange)
  React.useEffect(() => {
    onOpenChangeRef.current = onOpenChange
  }, [onOpenChange])
  React.useEffect(() => {
    onOpenChangeRef.current?.(open)
  }, [open])
}

function MultiSelectImpl(
  props: MultiSelectProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const {
    label,
    labelSize = 'sm',
    placeholder,
    searchPlaceholder,
    disabled = false,
    error = false,
    searchable = true,
    emptyMessage,
    loading,
    onOpenChange,
    onLoadMore,
    hasMore,
    isFetchingMore,
    className,
    id,
    name,
    allowCreate,
    onCreateOption,
    portal,
  } = props

  const reactId = React.useId()
  const controlId = id ?? reactId
  const listboxId = `${controlId}-listbox`

  const { state, handlers, selectedOptions, wrapperRef, panelRef, onKeyDown } =
    useMultiSelectApi(props, ref)

  useNotifyOpenChange(state.open, onOpenChange)

  return (
    <div ref={wrapperRef} className="relative flex w-full flex-col gap-0.5">
      {label && (
        <LabelRow label={label} labelSize={labelSize} htmlFor={controlId} />
      )}
      <Control
        selectedOptions={selectedOptions}
        placeholder={placeholder ?? searchPlaceholder}
        disabled={disabled}
        error={error}
        open={state.open}
        className={className}
        controlId={controlId}
        listboxId={listboxId}
        controlRef={state.controlRef}
        searchable={searchable}
        query={state.query}
        onQueryChange={state.setQuery}
        searchRef={state.searchRef}
        onInputKeyDown={onKeyDown}
        onOpen={() => state.setOpen(true)}
        onToggleOpen={handlers.toggleOpen}
        onRemove={handlers.remove}
      />
      {name && (
        <input type="hidden" name={name} value={state.selected.join(',')} />
      )}
      {state.open && (
        <MultiSelectDropdown
          state={state}
          handlers={handlers}
          listboxId={listboxId}
          portal={portal === true}
          panelRef={panelRef}
          emptyMessage={emptyMessage}
          loading={!!loading}
          hasMore={!!hasMore}
          isFetchingMore={!!isFetchingMore}
          onLoadMore={onLoadMore}
          allowCreate={allowCreate}
          onCreateOption={onCreateOption}
        />
      )}
    </div>
  )
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  MultiSelectImpl,
)
